import { useEffect, useId, useRef } from "react";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  PointElement,
  ScatterController,
  Tooltip,
} from "chart.js";
import type { ChartOptions, Plugin } from "chart.js";

import type { ConfigSummary } from "../api/types.gen";
import { formatCost, formatDuration, formatPercent, formatTokens } from "./format";
import { axisLabel, rowColor, rowGlyph, stateLabel } from "./present";

/**
 * The dashboard's charts.
 *
 * Chart.js is an npm dependency and is imported, not loaded from jsDelivr the
 * way the prototype did it. Batch 1 rejected a CDN charting library on the
 * deploy's origin posture, Cloudflare Pages serves our bundle anyway, and a
 * third-party script tag is one supply-chain compromise away from reading the
 * page it draws on. Only the controllers and scales actually used are
 * registered, which is what keeps the tree-shaken bundle to what is drawn.
 *
 * The accessibility work is the prototype's, kept verbatim because it is the
 * part of that file most worth keeping — and it is kept on EVERY variant, not
 * only the bar chart it started on. Every canvas is `role="img"` with a
 * `tabindex` and an `aria-describedby` pointing at a visually hidden paragraph
 * that states every row's value and coverage in words. A chart nobody can see
 * is not a chart with a caption missing; it is a chart with no content at all,
 * and the textual summary is the content. The `.chart-fallback` block behind
 * each canvas says the same thing when the library fails to load.
 *
 * Every number drawn here comes from `ConfigSummary`, which the server
 * computed. Nothing on this screen aggregates: see `internal/results`.
 */
Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  PointElement,
  ScatterController,
  Tooltip,
);

/** Which measurement a chart is drawing, which decides only its formatting. */
export type Metric = "index" | "cost" | "time" | "tokens" | "diff" | "turns" | "ratio";

/** The shape a chart is drawn in. */
export type ChartKind = "bar" | "stacked" | "diff" | "scatter";

/**
 * The numeric fields of a summary row a chart may read.
 *
 * A union of field names rather than an accessor function, because the chart
 * catalogue in ChartTabs.tsx is data — and data that carried closures would
 * stop being data.
 */
export type ValueField =
  | "agent_index"
  | "mean_agent_seconds"
  | "mean_reported_cost_usd"
  | "mean_total_tokens"
  | "mean_input_tokens"
  | "mean_output_tokens"
  | "mean_ai_changed_lines"
  | "mean_turns"
  | "mean_cache_hit_rate"
  | "mean_context_pct";

const METRIC_HELP: Record<Metric, string> = {
  index:
    "A task-normalized pass rate on a 0–100 scale. Attempts are averaged within each repository challenge, then every measured challenge receives equal weight.",
  time: "Average active agent wall time per measured challenge. Environment setup, verification, and harness-error time are excluded.",
  cost: "Average agent-reported USD cost per measured challenge, falling back to berbench's estimate where the agent reported none. Missing measurements are excluded, never counted as zero.",
  tokens:
    "Average tokens per measured challenge, split into prompt input, cached input, and generated output. Configurations whose agent published no usage are excluded rather than counted as zero.",
  diff: "Average changed lines (added plus removed) in the agent's patch, beside the accepted reference patch for the same challenges. A reference patch that does not exist is a gap, not a target the agent missed.",
  turns:
    "Average model turns in a measured run. Agents that produced no readable event stream report no turn count and are excluded.",
  ratio:
    "A share of a per-run quantity, averaged over the runs that measured it. Each run's own ratio is averaged — not the ratio of the totals, which would answer about the largest run rather than about the configuration.",
};

/**
 * What a specific ratio chart means, where the metric's own note is not enough.
 *
 * Cache read share is the one number on this screen whose obvious name is
 * wrong, so it is the one that gets a chart-level note. Keyed by ValueField
 * because the distinction is the field, not the metric.
 */
const FIELD_HELP: Partial<Record<ValueField, string>> = {
  mean_cache_hit_rate:
    "This is the share of the prompt that came from cache, not a cache hit rate. Cache CREATION tokens are not reported to this service at all, so a run that wrote a large cache and read none of it back is indistinguishable here from a run with no cache. Read it as how much of what the model was shown it did not have to be sent again.",
  mean_context_pct:
    "Peak context divided by the model's context window, per run, averaged. Runs whose tool never published a window — codex today — are excluded rather than drawn at zero.",
};

/**
 * How each field is named in prose, for the accessible summary of a scatter —
 * which has to say what its y axis is rather than assume the index.
 */
const FIELD_NOUN: Record<ValueField, string> = {
  agent_index: "index",
  mean_agent_seconds: "active time",
  mean_reported_cost_usd: "cost",
  mean_total_tokens: "total tokens",
  mean_input_tokens: "input tokens",
  mean_output_tokens: "output tokens",
  mean_ai_changed_lines: "changed lines",
  mean_turns: "turns",
  mean_cache_hit_rate: "prompt cache read share",
  mean_context_pct: "context used",
};

/** The value a chart reads off a row. `undefined` and `null` are one absence. */
export function valueOf(row: ConfigSummary, field: ValueField): number | null {
  return row[field] ?? null;
}

function formatValue(value: number | null, metric: Metric): string {
  if (value === null) return "unavailable";
  switch (metric) {
    case "index":
      return `${value.toFixed(0)} points`;
    case "time":
      return formatDuration(value);
    case "cost":
      return formatCost(value);
    case "tokens":
      return formatTokens(Math.round(value));
    case "diff":
      return `${Math.round(value)} lines`;
    case "ratio":
      return formatPercent(value);
    default:
      return value.toFixed(1);
  }
}

/** Compact text drawn on the canvas; the prose summary keeps the full units. */
function formatChartValue(value: number, metric: Metric): string {
  switch (metric) {
    case "index":
      return value.toFixed(0);
    case "diff":
      return String(Math.round(value));
    default:
      return formatValue(value, metric);
  }
}

/**
 * Fits a canvas label without hiding its full value from tooltips or assistive
 * technology. Measuring rather than cutting at a fixed character count matters
 * for model and option names, whose glyph widths vary considerably.
 */
export function fitCanvasLabel(
  text: string,
  maxWidth: number,
  measure: (candidate: string) => number,
): string {
  if (maxWidth <= 0) return "";
  if (measure(text) <= maxWidth) return text;

  const ellipsis = "…";
  if (measure(ellipsis) > maxWidth) return "";

  let low = 0;
  let high = text.length;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (measure(`${text.slice(0, middle).trimEnd()}${ellipsis}`) <= maxWidth) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }
  return `${text.slice(0, low).trimEnd()}${ellipsis}`;
}

/** The y-axis tick format, which is the metric's units without the noun. */
function formatTick(value: number, metric: Metric): string {
  switch (metric) {
    case "cost":
      return `$${value}`;
    case "time":
      return formatDuration(value);
    case "tokens":
      return formatTokens(Math.round(value));
    case "index":
      return value > 100 ? "" : String(value);
    case "ratio":
      return formatPercent(value);
    default:
      return String(value);
  }
}

/**
 * The textual summary, which is the chart's content for anyone not looking at
 * it. It names every row, its value, and whether it is part of the comparison —
 * because a screen reader user must not be told a number that the sighted
 * reader can see is greyed out and unranked.
 *
 * Each chart kind states what it actually draws. A stacked chart summarised as
 * one total would hide the split that is the entire point of stacking it, and a
 * scatter summarised on one axis would omit half the plot.
 */
export function chartSummary(
  rows: ConfigSummary[],
  metric: Metric,
  field: ValueField,
  kind: ChartKind = "bar",
  yField: ValueField = "agent_index",
  yMetric: Metric = "index",
): string {
  if (rows.length === 0) return "No configurations have been measured yet.";

  return rows
    .map((row) => {
      const name = configName(row);
      const state = stateLabel(row);
      switch (kind) {
        case "stacked":
          return (
            `${name}: ${formatValue(row.mean_input_tokens ?? null, "tokens")} input, ` +
            `${formatValue(row.mean_cached_tokens ?? null, "tokens")} cached, ` +
            `${formatValue(row.mean_output_tokens ?? null, "tokens")} output; ${state}.`
          );
        case "diff":
          return (
            `${name}: ${formatValue(row.mean_ai_changed_lines ?? null, "diff")} in the agent patch ` +
            `against ${formatValue(row.mean_gold_changed_lines ?? null, "diff")} in the reference patch; ${state}.`
          );
        case "scatter":
          // Both coordinates, and the y one named rather than assumed. A
          // scatter summarised on one axis omits half the plot, and a summary
          // that said "at index" for a chart whose y is output tokens would
          // read a different number to the one drawn.
          return (
            `${name}: ${formatValue(valueOf(row, field), metric)} ` +
            `at ${FIELD_NOUN[yField]} ${formatValue(valueOf(row, yField), yMetric)}; ${state}.`
          );
        default:
          return `${name}: ${formatValue(valueOf(row, field), metric)}; ${state}.`;
      }
    })
    .join(" ");
}

/** The row's name in prose — the axis label, joined. */
function configName(row: ConfigSummary): string {
  return axisLabel(row).filter(Boolean).join(" · ") || row.tool;
}

/** The stack's three colours, from the prototype. */
const TOKEN_COLORS = { input: "#291a40", cached: "#9970d8", output: "#e6a06d" };

/** The reference patch's colour: flat grey, because it is not a competitor. */
const GOLD_COLOR = "#d4d0da";

/**
 * Draws the vendor glyph under each bar and the value above it.
 *
 * A Chart.js plugin rather than a datalabels dependency: it is forty lines, and
 * the alternative is another package in the bundle for one label.
 *
 * On a stacked chart the label is the STACK TOTAL and it is drawn above the
 * top segment, because a per-segment label inside a thin band is unreadable and
 * three overlapping numbers are worse than none.
 */
const annotations: Plugin<"bar"> = {
  id: "benchmarkAnnotations",
  afterDatasetsDraw(chart, _args, options) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const opts = options as unknown as {
      rows: ConfigSummary[];
      metric: Metric;
      mini?: boolean;
      stacked?: boolean;
    };
    const rows = opts.rows ?? [];
    const sets = chart.data.datasets;
    const topMeta = chart.getDatasetMeta(sets.length - 1);

    ctx.save();
    const firstMeta = chart.getDatasetMeta(0);
    firstMeta.data.forEach((bar, index) => {
      const row = rows[index];
      if (!row) return;

      const total = opts.stacked
        ? sets.reduce((sum, set) => sum + Number(set.data[index] ?? 0), 0)
        : (sets[0].data[index] as number | null);
      if (total === null || total === undefined) return;

      const top = opts.stacked ? topMeta.data[index] : bar;
      // Inside the bar when there is room, above it when there is not — a
      // label printed over the gridlines of a two-pixel bar is unreadable.
      const inside = !opts.stacked && opts.metric === "index" && Number(total) >= 18;
      ctx.fillStyle = inside ? "#fff" : "#291c39";
      ctx.font = `600 ${opts.mini ? 11 : 12}px Inter, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        formatChartValue(Number(total), opts.metric),
        bar.x,
        inside ? top.y + 16 : Math.max(chartArea.top + 8, top.y - 9),
      );

      // A grouped chart's second bar gets its own label, in a lighter weight.
      // The reference patch beside an agent's is the whole point of that
      // chart, and an unlabelled grey bar makes the reader estimate it off the
      // axis. Skipped when stacked, where the segments share one column.
      if (!opts.stacked) {
        for (let set = 1; set < sets.length; set++) {
          const secondary = chart.getDatasetMeta(set).data[index];
          const value = sets[set].data[index] as number | null;
          if (secondary === undefined || value === null || value === undefined) continue;
          ctx.fillStyle = "#676073";
          ctx.font = `500 ${opts.mini ? 10 : 11}px Inter, sans-serif`;
          ctx.fillText(
            formatChartValue(Number(value), opts.metric),
            secondary.x,
            Math.max(chartArea.top + 9, secondary.y - 9),
          );
        }
      }

      // Mirror the prototype's axis treatment: compact cards retain the
      // rotated Chart.js ticks and only add a provider mark; full charts use a
      // stable, horizontal three-line label with the mark beside the agent.
      const last = topMeta.data[index];
      const labelX = !opts.stacked && sets.length > 1 ? (bar.x + last.x) / 2 : bar.x;
      if (opts.mini) {
        drawProviderMark(ctx, row, labelX, chartArea.bottom + 10, 10);
        return;
      }

      const categoryWidth = chartArea.width / Math.max(rows.length, 1);
      const labelWidth = Math.max(34, categoryWidth - 8);
      const fit = (text: string) =>
        fitCanvasLabel(text, labelWidth, (candidate) => ctx.measureText(candidate).width);
      const y = chartArea.bottom + 20;
      ctx.font = "650 12px Inter, sans-serif";
      const agent = fit(axisLabel(row)[0]);
      const agentWidth = ctx.measureText(agent).width;
      drawProviderMark(ctx, row, labelX - agentWidth / 2 - 10, y, 12);
      ctx.fillStyle = "#2a2730";
      ctx.fillText(agent, labelX, y);
      ctx.font = "600 11px Inter, sans-serif";
      ctx.fillText(fit(axisLabel(row)[1]), labelX, y + 18);
      ctx.fillStyle = "#807c87";
      ctx.font = "500 10px Inter, sans-serif";
      ctx.fillText(fit(axisLabel(row)[2]), labelX, y + 35);
    });
    ctx.restore();
  },
};

const PROVIDER_PATHS = {
  anthropic: "M17.304 3.541h-3.672L20.328 20.46H24ZM6.696 3.541 0 20.459h3.744l1.37-3.553h7.005l1.369 3.553h3.744L10.536 3.541Zm-.371 10.223 2.291-5.946 2.291 5.946Z",
  openai: "M22.282 9.821a6 6 0 0 0-.516-4.91 6.05 6.05 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a6 6 0 0 0-3.998 2.9 6.05 6.05 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.05 6.05 0 0 0 6.515 2.9A6 6 0 0 0 13.26 24a6.06 6.06 0 0 0 5.772-4.206 6 6 0 0 0 3.997-2.9 6.06 6.06 0 0 0-.747-7.073M13.26 22.43a4.48 4.48 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.8.8 0 0 0 .392-.681v-6.737l2.02 1.168a.07.07 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494M3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.77.77 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95A4.5 4.5 0 0 1 3.6 18.304M2.34 7.896a4.5 4.5 0 0 1 2.366-1.973V11.6a.77.77 0 0 0 .388.677l5.815 3.354-2.02 1.168a.08.08 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855-5.833-3.387L15.119 7.2a.08.08 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667m2.01-3.023-.141-.085-4.774-2.782a.78.78 0 0 0-.785 0L9.409 9.23V6.897a.07.07 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.8.8 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5Z",
} as const;

function drawProviderMark(
  ctx: CanvasRenderingContext2D,
  row: ConfigSummary,
  x: number,
  y: number,
  size: number,
) {
  const path =
    row.tool === "codex"
      ? PROVIDER_PATHS.openai
      : row.tool === "claude-code"
        ? PROVIDER_PATHS.anthropic
        : undefined;

  if (path !== undefined && typeof Path2D !== "undefined") {
    ctx.save();
    ctx.translate(x - size / 2, y - size / 2);
    ctx.scale(size / 24, size / 24);
    ctx.fillStyle = "#1d1b20";
    ctx.fill(new Path2D(path));
    ctx.restore();
    return;
  }

  ctx.fillStyle = "#948ba0";
  ctx.font = "700 11px Inter, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(rowGlyph(row), x, y);
}

/**
 * The scatter's quadrant wash, ported from the prototype.
 *
 * The shaded corner is a VERDICT — "better on both axes" — and it is only
 * drawn when there is one to give. That holds when y is the index (higher is
 * better) and x is a cost (lower is better), which is every scatter the
 * prototype had: up-and-left is the attractive corner.
 *
 * It does not hold for input against output tokens. Neither axis has a good
 * direction: an agent that reads little and writes little may be efficient or
 * may have given up, and one that reads a lot may be thorough or wasteful.
 * Washing a corner green there would be the chart asserting a conclusion the
 * measurement does not support, so `verdict` turns it off and only the
 * dividers are drawn. The dividers bisect the rendered plot, not the observed
 * data, which keeps all four quadrants geometrically equal even when the
 * measurements cluster at one end of an axis.
 */
const quadrant: Plugin<"scatter"> = {
  id: "quadrant",
  beforeDraw(chart, _args, options) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const verdict = (options as unknown as { verdict?: boolean }).verdict !== false;

    const splitX = chartArea.left + chartArea.width / 2;
    const splitY = chartArea.top + chartArea.height / 2;

    ctx.save();
    ctx.fillStyle = "#fcfbfc";
    ctx.fillRect(chartArea.left, chartArea.top, chartArea.width, chartArea.height);
    if (verdict) {
      ctx.fillStyle = "rgba(190,241,203,.5)";
      ctx.fillRect(
        chartArea.left,
        chartArea.top,
        Math.max(0, splitX - chartArea.left),
        Math.max(0, splitY - chartArea.top),
      );
    }
    ctx.setLineDash([3, 4]);
    ctx.strokeStyle = "#cdc9d3";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(splitX, chartArea.top);
    ctx.lineTo(splitX, chartArea.bottom);
    ctx.moveTo(chartArea.left, splitY);
    ctx.lineTo(chartArea.right, splitY);
    ctx.stroke();
    ctx.restore();
  },
};

/**
 * The scatter's point labels.
 *
 * The prototype had a hand-tuned offset table keyed by the ids of its fourteen
 * fixture rows. Real data has neither those ids nor that many points in the
 * same places, so labels are fitted to the available plot width and placed into
 * the first clear vertical lane. Their complete text remains in the tooltip and
 * accessible chart summary.
 */
const scatterLabels: Plugin<"scatter"> = {
  id: "scatterLabels",
  afterDatasetsDraw(chart, _args, options) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const rows = (options as unknown as { rows: ConfigSummary[] }).rows ?? [];

    ctx.save();
    ctx.font = "500 11px Inter, sans-serif";
    ctx.textBaseline = "middle";
    const occupied: Array<{ left: number; right: number; top: number; bottom: number }> = [];
    chart.getDatasetMeta(0).data.forEach((point, index) => {
      const row = rows[index];
      if (!row) return;

      const rightRoom = chartArea.right - point.x - 12;
      const leftRoom = point.x - chartArea.left - 12;
      const align: CanvasTextAlign = rightRoom >= leftRoom ? "left" : "right";
      const available = align === "left" ? rightRoom : leftRoom;
      const maxWidth = Math.max(
        48,
        Math.min(170, chartArea.width / 5, available),
      );
      const text = fitCanvasLabel(
        configName(row),
        maxWidth,
        (candidate) => ctx.measureText(candidate).width,
      );
      const width = ctx.measureText(text).width;
      const x = align === "left" ? point.x + 8 : point.x - 8;
      const left = align === "left" ? x : x - width;
      const right = align === "left" ? x + width : x;
      const lanes = index % 2 === 0
        ? [-14, 14, -28, 28, -42, 42, -56, 56, -70, 70, -84, 84]
        : [14, -14, 28, -28, 42, -42, 56, -56, 70, -70, 84, -84];
      let dy = lanes[0];
      let y = point.y + dy;
      for (const candidate of lanes) {
        const candidateY = Math.max(
          chartArea.top + 10,
          Math.min(chartArea.bottom - 10, point.y + candidate),
        );
        const box = { left: left - 3, right: right + 3, top: candidateY - 9, bottom: candidateY + 9 };
        if (!occupied.some((other) =>
          box.left < other.right && box.right > other.left &&
          box.top < other.bottom && box.bottom > other.top
        )) {
          dy = candidate;
          y = candidateY;
          break;
        }
      }
      occupied.push({ left: left - 3, right: right + 3, top: y - 9, bottom: y + 9 });

      ctx.textAlign = align;
      if (Math.abs(dy) > 16) {
        ctx.strokeStyle = "#b4afbd";
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(point.x, point.y);
        ctx.lineTo(point.x + (align === "left" ? 5 : -5), y);
        ctx.stroke();
      }
      ctx.lineWidth = 3.5;
      ctx.strokeStyle = "rgba(255,255,255,.97)";
      ctx.strokeText(text, x, y);
      ctx.fillStyle = "#584f64";
      ctx.fillText(text, x, y);
    });
    ctx.restore();
  },
};

/** The tooltip body every chart kind shares — the row's whole story. */
function tooltipLines(row: ConfigSummary): string[] {
  return [
    row.options.length > 0 ? row.options.join(" · ") : "default settings",
    `${stateLabel(row)}${row.rank > 0 ? ` · rank ${row.rank}` : ""}`,
    `Challenges measured: ${row.challenges}`,
    `Attempts: ${row.attempts} · harness errors: ${row.errored}`,
    `Index: ${formatValue(row.agent_index ?? null, "index")}`,
    `Active time: ${formatDuration(row.mean_agent_seconds ?? null)}`,
    `Cost: ${formatCost(row.mean_reported_cost_usd ?? null)}${
      row.cost_estimated ? " (estimated)" : " (agent-reported)"
    }`,
  ];
}

interface BenchmarkChartProps {
  rows: ConfigSummary[];
  metric: Metric;
  /** Which field a bar or the x axis of a scatter reads. */
  field: ValueField;
  /** Accessible name for the canvas. */
  title: string;
  kind?: ChartKind;
  /** The x-axis title, for a scatter. */
  xLabel?: string;
  /**
   * The scatter's y axis. It defaults to the index, which is what every
   * scatter the prototype had plotted; a chart that puts a second measurement
   * there — input against output tokens — names it, and in doing so also turns
   * off the quadrant wash, because up-and-left is only "better" when y is the
   * index and x is a cost.
   */
  yField?: ValueField;
  yMetric?: Metric;
  yLabel?: string;
  /** Compact variant for the highlight cards. */
  mini?: boolean;
}

/**
 * One chart, in whichever shape it was asked for.
 *
 * The chart instance is created in an effect and destroyed on cleanup, which
 * matters more than it looks: Chart.js attaches a resize observer and a canvas
 * context per instance, and a re-render that leaves the previous one alive
 * leaks both and draws two charts on one canvas.
 */
export function BenchmarkChart({
  rows,
  metric,
  field,
  title,
  kind = "bar",
  xLabel,
  yField = "agent_index",
  yMetric = "index",
  yLabel = "Coding Agent Index",
  mini = false,
}: BenchmarkChartProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const summaryID = useId();

  useEffect(() => {
    const element = canvas.current;
    if (element === null) return;

    const chart =
      kind === "scatter"
        ? makeScatter(element, rows, metric, field, xLabel ?? "", yField, yMetric, yLabel)
        : makeBars(element, rows, metric, field, kind, mini);

    return () => chart.destroy();
  }, [rows, metric, field, kind, xLabel, yField, yMetric, yLabel, mini]);

  return (
    <div
      className={
        mini ? "mini-chart" : kind === "scatter" ? "chart-wrap scatter-wrap" : "chart-wrap"
      }
    >
      <canvas
        ref={canvas}
        role="img"
        tabIndex={0}
        aria-label={title}
        aria-describedby={summaryID}
      />
      <p className="visually-hidden" id={summaryID}>
        {chartSummary(rows, metric, field, kind, yField, yMetric)}
      </p>
      <div className="chart-fallback">
        <div>
          <strong>Charts could not be loaded.</strong>
          <br />
          The exact textual summary remains available to assistive technology.
        </div>
      </div>
    </div>
  );
}

/** The bar family: one series, three stacked, or the agent-versus-gold pair. */
function makeBars(
  element: HTMLCanvasElement,
  rows: ConfigSummary[],
  metric: Metric,
  field: ValueField,
  kind: ChartKind,
  mini: boolean,
): Chart {
  const stacked = kind === "stacked";
  const legend = stacked || kind === "diff";

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 250 },
    layout: { padding: { top: mini ? 16 : 22, left: 8, right: 8 } },
    plugins: {
      legend: legend
        ? {
            display: true,
            position: "top",
            align: "start",
            labels: {
              usePointStyle: true,
              pointStyle: "rect",
              boxWidth: 8,
              boxHeight: 8,
              padding: 16,
              font: { family: "Inter", size: 12 },
            },
          }
        : { display: false },
      tooltip: {
        displayColors: legend,
        callbacks: {
          title: (items) => configName(rows[items[0].dataIndex]),
          label: (item) => {
            const row = rows[item.dataIndex];
            if (!row) return "";
            return tooltipLines(row);
          },
        },
      },
      // Typed as `any` at the boundary only: Chart.js has no way to know
      // about a plugin's own option shape, and the plugin above casts it back.
      benchmarkAnnotations: { rows, metric, mini, stacked },
    } as ChartOptions<"bar">["plugins"],
    scales: {
      x: {
        stacked,
        grid: { display: false },
        border: { display: false },
        afterFit: (axis) => {
          axis.height = mini ? 112 : 100;
        },
        ticks: {
          display: mini,
          color: "#242424",
          font: { family: "Inter", size: 10, weight: 500 },
          autoSkip: false,
          maxRotation: 48,
          minRotation: 48,
          padding: 22,
        },
      },
      y: {
        stacked,
        beginAtZero: true,
        // The index is a percentage and its axis says so; a 0–100 scale that
        // auto-fits to a maximum of 40 makes a bad setup look middling.
        max: metric === "index" ? 110 : undefined,
        grace: metric === "index" ? 0 : "12%",
        grid: { color: "#dddae2", drawTicks: false },
        border: { display: false },
        ticks: {
          display: !mini,
          color: "#837e8b",
          font: { family: "Inter", size: 12 },
          callback: (value) => formatTick(Number(value), metric),
        },
      },
    },
  };

  return new Chart(element, {
    type: "bar",
    plugins: [annotations],
    data: {
      labels: rows.map(axisLabel),
      datasets: datasetsFor(rows, field, kind),
    },
    options,
  });
}

function datasetsFor(rows: ConfigSummary[], field: ValueField, kind: ChartKind) {
  const square = { topLeft: 3, topRight: 3, bottomLeft: 0, bottomRight: 0 };

  if (kind === "stacked") {
    // Three series, bottom to top, so the cached band — usually the largest by
    // an order of magnitude — does not sit on top and swamp the rounding.
    return [
      {
        label: "Input",
        data: rows.map((row) => row.mean_input_tokens ?? null),
        backgroundColor: TOKEN_COLORS.input,
        borderRadius: 0,
      },
      {
        label: "Cached input",
        data: rows.map((row) => row.mean_cached_tokens ?? null),
        backgroundColor: TOKEN_COLORS.cached,
        borderRadius: 0,
      },
      {
        label: "Output",
        data: rows.map((row) => row.mean_output_tokens ?? null),
        backgroundColor: TOKEN_COLORS.output,
        borderRadius: square,
      },
    ];
  }

  if (kind === "diff") {
    // The agent's patch keeps its brand colour and the reference patch is flat
    // grey: the gold patch is the target, not a competitor, and colouring it
    // like one invites the reader to rank it.
    return [
      {
        label: "Agent patch",
        data: rows.map((row) => row.mean_ai_changed_lines ?? null),
        backgroundColor: rows.map(rowColor),
        borderRadius: 3,
      },
      {
        label: "Reference patch",
        data: rows.map((row) => row.mean_gold_changed_lines ?? null),
        backgroundColor: GOLD_COLOR,
        borderRadius: 3,
      },
    ];
  }

  return [
    {
      data: rows.map((row) => valueOf(row, field)),
      backgroundColor: rows.map(rowColor),
      borderRadius: square,
      borderSkipped: "bottom" as const,
      barPercentage: 0.76,
      categoryPercentage: 0.86,
    },
  ];
}

/**
 * Two measurements against each other, with the attractive quadrant shaded
 * where there is one.
 *
 * Only rows that have BOTH coordinates are plotted. A point at x=0 for a
 * configuration whose agent published no cost is not a cheap configuration, it
 * is a fabricated measurement — the same rule the bar charts follow by leaving
 * a gap. That now applies to the y axis too: it used to be the index and could
 * be assumed present, and a chart of input against output tokens has a second
 * field that can genuinely be missing.
 */
function makeScatter(
  element: HTMLCanvasElement,
  rows: ConfigSummary[],
  metric: Metric,
  field: ValueField,
  xLabel: string,
  yField: ValueField,
  yMetric: Metric,
  yLabel: string,
): Chart {
  const plotted = rows.filter(
    (row) => valueOf(row, field) !== null && valueOf(row, yField) !== null,
  );
  const xs = plotted.map((row) => valueOf(row, field) as number).sort((a, b) => a - b);
  const maxX = xs.length > 0 ? xs[xs.length - 1] : 1;
  const ys = plotted.map((row) => valueOf(row, yField) as number).sort((a, b) => a - b);
  const maxY = ys.length > 0 ? ys[ys.length - 1] : 1;

  // The index is a 0–100 scale and its axis says so, for the same reason the
  // bar charts fix it: an auto-fitted axis makes a bad setup look middling.
  // Any other y is a measurement with no ceiling and is left to fit.
  const indexY = yField === "agent_index";

  return new Chart(element, {
    type: "scatter",
    plugins: [quadrant, scatterLabels],
    data: {
      datasets: [
        {
          data: plotted.map((row) => ({
            x: valueOf(row, field) as number,
            y: valueOf(row, yField) as number,
          })),
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: plotted.map(rowColor),
          pointBorderColor: "#fff",
          pointBorderWidth: 1.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 250 },
      layout: { padding: { top: 42, right: 22, left: 4 } },
      plugins: {
        legend: { display: false },
        // The wash is a claim that one corner is better. It is true when y is
        // the index and x is a cost, and false for input against output
        // tokens, where neither axis has a good direction.
        quadrant: { verdict: indexY },
        scatterLabels: { rows: plotted },
        tooltip: {
          displayColors: false,
          callbacks: {
            title: (items) => configName(plotted[items[0].dataIndex]),
            label: (item) => tooltipLines(plotted[item.dataIndex]),
          },
        },
      } as ChartOptions<"scatter">["plugins"],
      scales: {
        x: {
          beginAtZero: true,
          suggestedMax: maxX * 1.12,
          title: {
            display: true,
            text: xLabel,
            color: "#2d2933",
            font: { weight: 600, size: 13 },
          },
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: "#6f6a79",
            font: { family: "Inter", size: 12 },
            callback: (value) => formatTick(Number(value), metric),
          },
        },
        y: {
          beginAtZero: true,
          max: indexY ? 110 : undefined,
          suggestedMax: indexY ? undefined : maxY * 1.12,
          title: {
            display: true,
            text: yLabel,
            color: "#2d2933",
            font: { weight: 600, size: 13 },
          },
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: "#6f6a79",
            font: { family: "Inter", size: 12 },
            callback: (value) => formatTick(Number(value), yMetric),
          },
        },
      },
    },
  });
}

/**
 * The coverage key and methodology note under every chart.
 *
 * `field` is optional and only changes the note: two of the ratio charts mean
 * something narrower than "a share", and saying which belongs where the reader
 * is already asking what the metric means.
 */
export function ChartFoot({
  metric,
  rows,
  field,
}: {
  metric: Metric;
  rows: ConfigSummary[];
  field?: ValueField;
}) {
  const comparable = rows.filter((row) => row.coverage_state === "comparable").length;

  return (
    <div className="chart-foot">
      <div className="coverage-key">
        <span>
          <i />
          Comparable
        </span>
        <span className="limited">
          <i />
          Limited
        </span>
        <span className="unavailable">
          <i />
          Unavailable
        </span>
        <span className="coverage-count">
          {comparable} of {rows.length} comparable
        </span>
      </div>
      <details className="metric-help">
        <summary>
          What this metric means <span>+</span>
        </summary>
        <p>{(field !== undefined && FIELD_HELP[field]) || METRIC_HELP[metric]}</p>
      </details>
    </div>
  );
}
